import { useEffect, useState } from "react";
import CoreSelect from "../../../../app/core/components/CoreSelect/CoreSelect";
import CoreInputField from "../../../../app/core/components/CoreInputField/CoreInputField";
import CoreButton from "../../../../app/core/components/CoreButton/CoreButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { jwtDecode } from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import { doGetStudioById } from "../loginActions";
import { toast } from "react-toastify";
import { ROLE } from "../../../../utils/constants/profiles";
import { actions as appActions } from "../../../../app/appSlice";
import {
  useLoginMutation,
  useUserLoginMutation,
} from "../../../../features/api/admin/authApi";
import { isEmailValid } from "../../../../utils/functions/form";
import { t } from "i18next";
import { useLoginStatusMutation } from "../../../../features/api/user/userAccount";

const LoginForm = () => {
  const PROFILE_LIST = {
    PROFESSIONISTA: t("Admin"),
    CONTRIBUENTE: t("User"),
  };

  const appConfig = useSelector((state) => state.app.appConfig);
  const otpActive = appConfig?.modules?.login?.otp;
  const requireUser = appConfig?.modules?.profile;
  const role = useSelector((state) => state.app.role);
  const isAuth = useSelector((state) => Boolean(state.app.accessToken));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [selectedProfile, setSelectedProfile] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [formError, setFormError] = useState({});
  const [otpErrorText, setOtpErrorText] = useState("");
  const [disabledField, setIsDisabledField] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [login, { isLoading: loadingAdmin }] = useLoginMutation();
  const [userLogin, { isLoading: loadingUser }] = useUserLoginMutation();
  const [loginStatus] = useLoginStatusMutation();

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (isAuth && appConfig) {
      setTimeout(() => {
        if (role === ROLE.USER) {
          if (isProfileComplete) {
            navigate(`/auth/user/${appConfig?.mainSection}`);
          } else {
            navigate(`/auth/user/${t("profile")}`);
          }
        } else if (role !== ROLE.USER) {
          navigate(`/auth/admin/${appConfig?.mainSection}`);
        } else {
          console.error(
            "isAuth è true, ma non sei stato redirezionato! ruolo sconosciuto"
          );
        }
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, appConfig]);

  function isFormError() {
    let errors = {};
    if (!isEmailValid(email)) errors.email = t("InsertValidMail");
    if (!password) errors.password = t("InsertPassword");
    if (!selectedProfile) errors.selectedProfile = t("ChooseProfile");
    if (otpActive && showOtp) {
      if (!otp) {
        setOtpErrorText(t("InsertOTP"));
      }
    }
    setFormError(errors);
    return Object.entries(errors).length > 0;
  }

  const submit = (event) => {
    event.preventDefault();
    let decodeJWT;
    let JWT;

    const body = {
      email: email,
      password: password,
      twoFactorCode: otp,
      twoFactorRecoveryCode: "",
    };
    if (isFormError()) return;
    if (PROFILE_LIST.PROFESSIONISTA === selectedProfile) {
      login(body)
        .then((response) => {
          if (response.data) {
            decodeJWT = jwtDecode(response.data);
            JWT = response.data;
            dispatch(
              appActions.updateAppState({ accessToken: JWT, ...decodeJWT })
            );
            !decodeJWT.adminStudios[0].capabilities.some((cap) =>
              cap.includes(ROLE.ADMIN)
            ) &&
              dispatch(
                doGetStudioById({
                  studioId: decodeJWT.adminStudios[0].studioId,
                  token: JWT,
                })
              );
          } else if (response.error?.status === 403) {
            setShowOtp(true);
            setIsDisabledField(true);
            toast.info(t("OTPsended"));
          } else if (otpActive && showOtp && response.error?.status === 401)
            toast.error(t("OTPerror"));
          else toast.error(t("EmailorPasswordWrong"));
        })
        .catch((error) => {
          toast.error(error);
        });
    } else {
      userLogin(body)
        .then((response) => {
          if (response.data) {
            decodeJWT = jwtDecode(response.data);
            JWT = response.data;
            handelChestStatus(JWT, decodeJWT.adminStudios[0].studioId);
            // Salva il token nello stato Redux
            dispatch(
              appActions.updateAppState({ accessToken: JWT, ...decodeJWT })
            );
          } else {
            toast.error(t("EmailorPasswordWrong"));
          }
        })
        .catch((error) => {
          toast.error(error);
        });
    }
  };

  const handelChestStatus = (jwt, stId) => {
    loginStatus({ token: jwt, studioId: stId })
      .unwrap()
      .then((res) => {
        if (res.Result === 1) {
          const profileData = Object.keys(requireUser).reduce((acc, key) => {
            if (requireUser[key]) {
              acc[key] = res.Data?.[key];
            }
            return acc;
          }, {});
          dispatch(appActions.setProfileData(profileData));
          dispatch(
            appActions.setLogoImageURL({
              logoImageURL: res.Data?.logoUrl,
            })
          );
          dispatch(
            appActions.setColors({
              primaryColor: res.Data?.primaryColor,
              secondaryColor: res.Data?.secondaryColor,
            })
          );
        }
        // Controlla i campi richiesti a BE checkstatus
        setIsProfileComplete(res.Data?.isProfileComplete);
      })
      .catch((err) => {
        console.error("Errore nella chiamata loginStatus:", err);
      });
  };

  return (
    <Box>
      <Box component="form" className="flex flex-col w-full items-center">
        <CoreSelect
          className="w-full"
          onChange={(e) => {
            setSelectedProfile(e.target.value);
            setFormError((prev) => ({ ...prev, selectedProfile: "" }));
          }}
          field={selectedProfile}
          label={t("Profile")}
          error={Boolean(formError.selectedProfile)}
          helperText={formError.selectedProfile}
          required={true}
          disabled={disabledField}
          defaultValue=""
          items={PROFILE_LIST}
        />

        <Box className="flex w-full flex-col items-center mt-2">
          <CoreInputField
            className="w-full"
            required={true}
            value={email}
            error={Boolean(formError.email)}
            helperText={formError.email}
            disabled={disabledField}
            onChange={(e) => {
              setEmail(e.target.value.trim());
              setFormError((prev) => ({ ...prev, email: "" }));
            }}
            label="Email"
            type="email"
          />
          <CoreInputField
            className="w-full"
            disabled={disabledField}
            required={true}
            value={password}
            error={Boolean(formError.password)}
            helperText={formError.password}
            onChange={(e) => {
              setPassword(e.target.value.trim());
              setFormError((prev) => ({ ...prev, password: "" }));
            }}
            type={showPassword ? "text" : "password"}
            label="Password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {otpActive && showOtp && (
            <CoreInputField
              className="w-full"
              required={true}
              error={Boolean(otpErrorText)} // Verifica se c'è un errore
              helperText={otpErrorText} // Mostra il messaggio di errore
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value);
                setOtpErrorText("");
              }}
              label="OTP"
            />
          )}
        </Box>

        <Box className="flex w-full justify-between mt-16 mb-2 space-x-2">
          <CoreButton
            className="btn-round-outline w-1/2 "
            disabled={disabledField}
            testo={t("Register")}
            onClick={() => navigate("/registration")}
          />
          <CoreButton
            disabled={loadingAdmin || loadingUser}
            className="btn-round w-1/2"
            type="submit"
            testo={
              loadingAdmin || loadingUser ? (
                <>
                  <CircularProgress size={20} className="text-white " />{" "}
                  <span className="mx-4">{t("Wait")}</span>{" "}
                </>
              ) : (
                t("Login")
              )
            }
            onClick={submit}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default LoginForm;
